import { CVBuilderFormValues } from 'utils/cv-form-db';
import { GuestPrefill } from './api-types';

type PersonalDetails = CVBuilderFormValues['personalDetails'];

// Only the personal-detail fields Supply can provide; everything else on the CV
// (insurance, availability, BAföG, skills, …) has no personnel source.
const PREFILLABLE_FIELDS = [
  'firstname',
  'lastname',
  'email',
  'phone',
  'zip'
] as const;

/**
 * Non-destructive merge of Supply prefill suggestions into the personal-details form values:
 * fills ONLY fields that are currently empty, so anything already present — a returning guest's
 * saved CV, or text the applicant typed — always wins. Never mutates the input (when there is
 * nothing to fill, the original object is returned unchanged).
 */
export const mergePrefill = (
  personalDetails: PersonalDetails,
  prefill: GuestPrefill | null | undefined
): PersonalDetails => {
  if (!prefill) {
    return personalDetails;
  }
  const merged = { ...personalDetails };
  PREFILLABLE_FIELDS.forEach(field => {
    const current = merged[field];
    const suggestion = prefill[field];
    if ((current === undefined || current === '') && suggestion) {
      merged[field] = suggestion;
    }
  });
  return merged;
};

/**
 * Whether any prefillable personal field is empty — i.e. whether there's anything for prefill to
 * fill. Used to decide whether to fetch/seed prefill: true for a brand-new guest AND for a guest
 * with a partial CV whose personal fields are blank (a CV row gets created on first autosave with
 * coerced defaults, so "a CV exists" is NOT a reliable "has personal data" signal). Returns false
 * only when every personal field is already populated — then prefill is skipped (no fetch/delay).
 */
export const hasEmptyPrefillableField = (
  personalDetails: PersonalDetails | undefined
): boolean => {
  if (!personalDetails) {
    return true;
  }
  return PREFILLABLE_FIELDS.some(field => {
    const value = personalDetails[field];
    return value === undefined || value === '';
  });
};
